import { useEffect, useRef, useState } from 'react';
import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useAuth } from '../../context/wms-context';
import config from '../../../utils/config';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

// Components
import LoadingSpinner from '../../../components/LoadingSpinner';
import SimpleLogo from '../../../assets/images/simpleLogo.jpg';
import Notification from '../../../components/Notification';
import Modal from '../../../components/Modal';

export default function BinderCheckPanel({
    activeDocument,
    inventoryLineId,
    loadingDocument,
    pacAirData,
    setShowSavedNotification,
    setStatus,
    showSavedNotification,
    status,
}) {
    const { currentUser } = useAuth();
    const [conditionMatch, setConditionMatch] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const ORIGIN = config.origin;

    const partNumber =
        currentUser?.selectedMaidLine?.inscor__Part_Master__rName || currentUser?.receivingFormData?.PartNumber;
    const conditionCode =
        currentUser?.selectedMaidLine?.inscor__Condition_Code__rName || currentUser?.receivingFormData?.ConditionCode;

    const dialogRef = useRef(null);

    const handleCloseNotification = () => {
        setShowSavedNotification({
            show: false,
            title: '',
            desc: '',
            status: '',
        });
    };

    useEffect(() => {
        if (showSavedNotification.show) {
            setTimeout(handleCloseNotification, 5000);
        }
    }, [showSavedNotification.show]);

    const handleRowClick = (document) => {
        setTimeout(() => {
            document.element?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 100);
    };

    useEffect(() => {
        function handleBackdropClick(event) {
            if (dialogRef.current && dialogRef.current.contains(event.target)) {
                event.stopPropagation();
            }
        }

        document.addEventListener('click', handleBackdropClick);

        return () => {
            document.removeEventListener('click', handleBackdropClick);
        };
    }, []);

    const continueToAction = () => {
        setStatus({
            ...status,
            binderCheckPanel: false,
            actionPanel: true,
        });
    };

    const continueNonMatch = () => {
        setShowConfirmModal(true);
    };

    const confirmContinue = async () => {
        // Mark inventory line as Protected with "Not In Binder" as reason
        try {
            const url = `${ORIGIN}/wms/receiving/mark-as-protected`;

            const dataToSend = {
                authentication: {
                    userId: currentUser.authentication.userId,
                    userEmail: currentUser.authentication.userEmail,
                    userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
                },
                inventoryLineId: inventoryLineId,
                reason: 'Not In Binder',
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                return null;
            }

            continueToAction();
        } catch (error) {
            return null;
        }
    };

    const cancelButtonStyling =
        'mt-3 inline-flex w-full justify-center rounded-md bg-gray-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-400 sm:col-start-1 sm:mt-0';
    const confirmButtonStyling =
        'inline-flex w-full justify-center rounded-md bg-blue-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-primary sm:col-start-2';

    const titleText = 'Are you sure?';
    const bodyText =
        'You are about to mark this Inventory Line as Protected due to not finding the part number on any of the documents with a matching Condition Code.';

    return (
        <>
            <Transition.Root show={status.binderCheckPanel} as={Fragment}>
                <Dialog as="div" className="relative z-10" open={status.binderCheckPanel} onClose={() => {}}>
                    <Notification
                        showSavedNotification={showSavedNotification.show}
                        setShowSavedNotification={handleCloseNotification}
                        title={showSavedNotification.title}
                        desc={showSavedNotification.desc}
                        status={showSavedNotification.status}
                    />
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-90" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:w-full sm:max-w-9xl min-h-96">
                                    <nav className="flex justify-center p-4 py-2 bg-blue-primary">
                                        <h1 className="text-2xl text-center text-white">Binder Check</h1>
                                    </nav>
                                    <div className="flex flex-grow p-4">
                                        <div className="w-[30%] border-gray-300">
                                            <h2 className="mb-6 text-2xl font-semibold text-center underline">
                                                Table of Contents
                                            </h2>
                                            <div className="h-full overflow-auto">
                                                <table className="w-full border">
                                                    <thead className="sticky top-0 border bg-blue-primary border-blue-primary">
                                                        <tr className="w-full">
                                                            <th className="w-full p-2 text-left text-white">
                                                                Document Name
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className={loadingDocument ? 'pointer-events-none' : ''}>
                                                        {pacAirData?.documents?.length ? (
                                                            pacAirData?.documents?.map((item, index) => (
                                                                <DocumentRow
                                                                    item={item}
                                                                    index={index}
                                                                    active={activeDocument?.documentId === item.documentId}
                                                                    handleRowClick={handleRowClick}
                                                                />
                                                            ))
                                                        ) : (
                                                            <tr>
                                                                {pacAirData?.loading ? (
                                                                    <td className="p-2 text-center">Loading Documents...</td>
                                                                ) : (
                                                                    <td className="p-2 text-center">
                                                                        No documents were found for this part number.
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div className="w-[70%] pl-4 flex-grow flex items-center justify-center sticky left-[30%] top-[1%] h-[93vh]">
                                            <div className="flex flex-col items-center justify-center w-full h-full">
                                                <h2 className="mb-6 text-2xl font-semibold text-center">
                                                    Part Number: {partNumber}
                                                </h2>
                                                {activeDocument?.error ? (
                                                    <div className="relative flex items-center justify-center w-full h-full border-2 border-gray-300">
                                                        <div className="text-center">
                                                            <div className="flex justify-center">
                                                                <img
                                                                    className="w-auto h-40"
                                                                    src={SimpleLogo}
                                                                    alt="Pacific Air Logo"
                                                                />
                                                            </div>
                                                            <p className="text-base font-semibold text-indigo-600">404</p>
                                                            <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
                                                                Error loading document.
                                                            </h1>
                                                            <p className="mt-6 text-base leading-7 text-gray-600">
                                                                There was an error loading the document from this URL.
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : !loadingDocument ? (
                                                    <div className="flex flex-col items-center justify-center w-full h-full">
                                                        <div className="relative flex items-center justify-center w-full h-full border-2 border-gray-300">
                                                            {activeDocument?.empty ? (
                                                                <p className="text-xl font-semibold text-center underline">
                                                                    No document was found.
                                                                </p>
                                                            ) : (
                                                                <>
                                                                    <p className="absolute text-xl font-semibold text-center">
                                                                        Failed to fetch, or document is too large.
                                                                        <br />
                                                                        Please click the Dropbox link to the document to view
                                                                        it.
                                                                    </p>
                                                                    <embed
                                                                        src={`data:application/pdf;base64,${activeDocument?.pdf}`}
                                                                        type="application/pdf"
                                                                        className="relative z-10 w-full h-full"
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center w-full h-screen">
                                                        <LoadingSpinner />
                                                        <p className="mt-20 ml-4 text-2xl font-semibold">
                                                            Loading Document...
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-row items-center justify-end gap-2 px-4">
                                        <input
                                            type="checkbox"
                                            disabled={activeDocument?.empty || loadingDocument}
                                            name="conditionMatch"
                                            id="conditionMatch"
                                            className="w-4 h-4"
                                            checked={conditionMatch}
                                            onChange={() => {
                                                setConditionMatch(!conditionMatch);
                                            }}
                                        ></input>
                                        <label
                                            for="conditionMatch"
                                            className={`text-xl m-0 p-0 ${
                                                (activeDocument?.empty || loadingDocument) && 'text-gray-400'
                                            }`}
                                        >
                                            Inventory Part Number Matches Condition Code (<strong>{conditionCode}</strong>)
                                        </label>
                                    </div>
                                    <div className="flex flex-row justify-end p-4 space-x-4">
                                        {!inventoryLineId ? (
                                            <LoadingSpinner spinnerSize={8} className={''} />
                                        ) : (
                                            <button
                                                type="button"
                                                disabled={loadingDocument || !inventoryLineId}
                                                className={`px-3 py-1.5 w-48 text-lg font-medium text-white rounded-md border border-transparent shadow-sm ${
                                                    conditionMatch
                                                        ? 'bg-green-600 hover:bg-green-500'
                                                        : 'bg-red-600 hover:bg-red-500'
                                                } focus:outline-none focus:ring-2 focus:ring-blue-primary focus:ring-offset-2`}
                                                onClick={conditionMatch ? continueToAction : continueNonMatch}
                                            >
                                                Continue
                                            </button>
                                        )}
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            <Modal
                open={showConfirmModal}
                setOpen={setShowConfirmModal}
                title={titleText}
                body={bodyText}
                onConfirm={confirmContinue}
                confirmButtonStyling={confirmButtonStyling}
                cancelButtonStyling={cancelButtonStyling}
            />
        </>
    );
}

function DocumentRow({ item, index, active }) {
    return (
        <tr
            className={`flex flex-col items-center w-full cursor-pointer ${active ? 'border-2 border-gray-400' : ''}`}
            onClick={() => {}}
            id={`doc-row-${index}`}
            key={index}
        >
            <div
                key={index}
                className={`border-b ${index % 2 === 1 ? 'bg-gray-200' : ''} flex justify-between items-center w-full`}
            >
                <div className="p-2 ml-4">
                    <a
                        href={item.documentLink ?? '#'}
                        className="text-blue-500 hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {item.name}
                    </a>
                    <div className="text-sm text-gray-600">{item.documentLocation}</div>
                </div>
                <div className="flex items-center justify-between w-16 mr-4">
                    {item.pdf ? <CheckIcon className="w-5 h-5 text-green-500" /> : null}
                    <button className="p-1 border rounded">
                        {active ? <ChevronDownIcon className="w-5 h-5" /> : <ChevronRightIcon className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            {active ? (
                <div className="w-[95%] mb-2">
                    <div className="w-full border">
                        <div className="sticky top-0 bg-gray-200 border">
                            <div className="flex justify-around font-bold">
                                <h3 className="p-2 text-left">Page #</h3>
                                <h3 className="p-2 text-right">From Page</h3>
                            </div>
                        </div>
                        <div>
                            {item?.pages?.map((page, index) => (
                                <div className="flex justify-around">
                                    <p className="p-2 text-left">{index + 1}</p>
                                    <p className="p-2 text-right">{page}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </tr>
    );
}
